/**
 * Hook: liefert die Liste der Filialen, die der User
 *   (a) zugänglich hat (laut /me)
 *   (b) tatsächlich Daten in der Statistik haben (laut /dimensions)
 * sortiert nach store_number numerisch (1, 2, 3, … 10, 11, … statt 1, 10, 100).
 *
 * So sehen alle Berichts-Filter genau die Filialen, die im DRS-Cockpit
 * auch tatsächlich Aktivität zeigen — keine Karteileichen mehr.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type MeResponse } from './api.ts';

export interface AvailableStore {
  id: number;
  store_number: string;
  name: string;
}

export function useAvailableStores(): AvailableStore[] {
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: api.me });
  const { data: dims } = useQuery({
    queryKey: ['dimensions'],
    queryFn: api.dimensions,
  });

  return useMemo(() => {
    if (!me?.stores) return [];
    const storesWithData = new Set<string>();
    const storeNrDim = dims?.find((d) => d.dimension === 'store_number');
    if (storeNrDim) {
      for (const v of storeNrDim.values) storesWithData.add(String(v.value));
    }
    const filtered =
      storesWithData.size > 0
        ? me.stores.filter((s) => storesWithData.has(s.store_number))
        : me.stores;
    return [...filtered].sort((a, b) => {
      const na = Number(a.store_number);
      const nb = Number(b.store_number);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.store_number.localeCompare(b.store_number, 'de');
    });
  }, [me, dims]);
}
